import React, { useEffect, useState } from "react";
import {
  AppBar,
  Avatar,
  Grid,
  Typography,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Box,
  useMediaQuery,
} from "@mui/material";
import { tabsClasses } from "@mui/material/Tabs";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context";
import LanguageSelector from "../../../lang/LanguageSelector";
import { useTranslation } from "react-i18next";
import { getAccess } from "../../../permissions/Permissions";
import { useTheme } from "@mui/material/styles";
import useFetchObjects from "../../../api/useFetchObjects";

const tabStyle = {
  color: "white",
  textTransform: "capitalize",
  fontWeight: "600",
  padding: "8px 12px",
  borderRadius: "8px",
  margin: "0px 2px",
  transition: "background-color 0.3s ease, color 0.3s ease",
};

const selectedTabStyle = {
  backgroundColor: "transparent",
  borderRadius: "8px",
  color: "white",
  margin: "0px 2px",
  fontWeight: "700",
  border: "3px solid white",
  borderBottom: "5px solid white",
};

const NavBar = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const user = auth?.user;

  const { data } = useFetchObjects(
    ["permissions-users"],
    `users/${user?.user?.id}/`,
    token,
  );

  const transformPermissions = (data) => {
    if (!data) return {};

    return {
      reception: data.reception || false,
      pharmacy: data.pharmacy || false,
      doctor: data.doctor || false,
      nurse: data.nurse || false,
      x_ray: data.x_ray || false,
      ultrasounds: data.ultrasounds || false,
      laboratory: data.laboratory || false,
      endoscopy: data.endoscopy || false,
      ct_scan: data.ct_scan || false,
      m_roy: data.m_roy || false,
      echo: data.echo || false,
      finance: data.finance || false,
      financial_manager: data.financial_manager || false,
      hr: data.hr || false,
      academic: data.academic || false,
      dental: data.dental || false,
      call_center: data.call_center || false,
      limited_data_view: data.limited_data_view || false,
      is_manager_in_call_center: data.is_manager_in_call_center || false,
      beauty: data.beauty || false,
      htPatient: data.htPatient || false,
      operations: data.operations || false,
      emergency: data.emergency || false,
      bedManagement: data.bedManagement || false,
      ipd: data.ipd || false,
      icu: data.icu || false,
      reports: data.reports || false,
      night_shift: data.night_shift || false,
      night_shift_manage: data.night_shift_manage || false,
    };
  };

  // const permissions = transformPermissions(data);

  const firstname = user?.user?.username;
  const permissions = getAccess(
    transformPermissions(data),
    user?.user?.isAdmin,
  );

  const [value, setValue] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogoutClick = () => {
    auth.logout();
    navigate("/user/sign-in/");
    handleMenuClose();
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    navigate(newValue);
  };

  useEffect(() => {
    const topLevelPath = location.pathname.split("/")[1];
    const newValue = `/${topLevelPath}`;

    // Only set value if it matches one of the available tabs
    const availableTabs = [
      "/reception",
      "/pharmacy",
      "/doctor",
      "/nurse",
      "/x-rays",
      "/ultra-sound",
      "/laboratory",
      "/endoscopy",
      "/ct-scan",
      "/echocardiography",
      "/finance",
      "/financial-manager",
      "/reports",
      "/human-resource",
      "/mri",
      "/academic",
      "/dental",
      "/night-shift",
      "/operations",
      "/emergency",
      "/bed-management",
      "/ipd",
      "/icu",
      "/patient-profile",
    ];

    if (availableTabs.includes(newValue)) {
      setValue(newValue);
    } else {
      // For home page or unknown routes, don't set any tab as active
      setValue(false);
    }
  }, [location.pathname]);

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: theme.palette.primary,
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        minHeight: "10vh", // Reduced height
        padding: isSmallScreen ? "8px" : "7px",
      }}
    >
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ paddingX: isSmallScreen ? "8px" : "16px", height: "100%" }}
      >
        {/* Row for Profile and Language Selector */}
        <Grid
          item
          xs={12}
          md={1}
          display={"flex"}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "white",
              borderRadius: "50%",
              padding: "4px",
            }}
          >
            <img
              src="/daudzai-logo.png"
              alt="Company Logo"
              style={{
                height: isExtraSmallScreen ? "50px" : "70px",
                width: "auto",
                borderRadius: "50%",
                padding: "4px",
                cursor: "pointer",
              }}
              onClick={() => {
                navigate("/");
              }}
            />
          </Box>

          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <LanguageSelector />
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  fontSize: isExtraSmallScreen ? "0.875rem" : "1rem",
                  margin: "0px 5px",
                }} // Responsive font size
              >
                {firstname}
              </Typography>
              <Avatar
                onClick={handleMenuClick}
                sx={{
                  cursor: "pointer",
                  boxShadow: "0 0 5px rgba(0, 0, 0, 0.3)",
                  height: isExtraSmallScreen ? "30px" : "40px", // Responsive size
                  width: isExtraSmallScreen ? "30px" : "40px",
                }}
              />
            </Box>
          </Box>
        </Grid>

        {/* Row for Tabs */}
        <Grid item xs={12} md={9}>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              indicatorColor="none"
              sx={{
                [`& .${tabsClasses.scrollButtons}`]: {
                  "&.Mui-disabled": { opacity: 0.3 },
                },
              }}
            >
              {permissions.reception && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/reception" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.reception")}
                  value="/reception"
                  component={NavLink}
                  to="/reception"
                />
              )}
              {permissions.pharmacy && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/pharmacy" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.pharmacy")}
                  value="/pharmacy"
                  component={NavLink}
                  to="/pharmacy"
                />
              )}
              {permissions.doctor && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/doctor" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.doctor")}
                  value="/doctor"
                  component={NavLink}
                  to="/doctor"
                />
              )}
              {permissions.nurse && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/nurse" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.nurse")}
                  value="/nurse"
                  component={NavLink}
                  to="/nurse"
                />
              )}
              {permissions.x_ray && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/x-rays" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.x-rays")}
                  value="/x-rays"
                  component={NavLink}
                  to="/x-rays"
                />
              )}
              {permissions.ultrasounds && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/ultra-sound" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.ultra-sound")}
                  value="/ultra-sound"
                  component={NavLink}
                  to="/ultra-sound"
                />
              )}
              {permissions.laboratory && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/laboratory" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.laboratory")}
                  value="/laboratory"
                  component={NavLink}
                  to="/laboratory"
                />
              )}
              {permissions.endoscopy && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/endoscopy" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.endoscopy", "Endoscopy")}
                  value="/endoscopy"
                  component={NavLink}
                  to="/endoscopy"
                />
              )}
              {permissions.ct_scan && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/ct-scan" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.ctScan", "CT Scan")}
                  value="/ct-scan"
                  component={NavLink}
                  to="/ct-scan"
                />
              )}
              {permissions.m_roy && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/mri" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.mri", "MRI")}
                  value="/mri"
                  component={NavLink}
                  to="/mri"
                />
              )}
              {permissions.echo && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/echocardiography" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.echocardiography")}
                  value="/echocardiography"
                  component={NavLink}
                  to="/echocardiography"
                />
              )}
              {permissions.finance && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/finance" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.finance")}
                  value="/finance"
                  component={NavLink}
                  to="/finance"
                />
              )}
              {permissions.financial_manager && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/financial-manager" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.financialManager", "Financial Manager")}
                  value="/financial-manager"
                  component={NavLink}
                  to="/financial-manager"
                />
              )}
              {permissions.reports && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/reports" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.reports")}
                  value="/reports"
                  component={NavLink}
                  to="/reports"
                />
              )}
              {permissions.hr && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/human-resource" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.hr")}
                  value="/human-resource"
                  component={NavLink}
                  to="/human-resource"
                />
              )}
              {permissions.academic && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/academic" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.academic", "Academic")}
                  value="/academic"
                  component={NavLink}
                  to="/academic"
                />
              )}
              {permissions.dental && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/dental" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.dental")}
                  value="/dental"
                  component={NavLink}
                  to="/dental"
                />
              )}
              {permissions.night_shift && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/night-shift" ? selectedTabStyle : {}),
                  }}
                  label={t("nightShift.nav.title", "Night shift")}
                  value="/night-shift"
                  component={NavLink}
                  to="/night-shift"
                />
              )}
              {permissions?.call_center && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/call-center" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.callCenter")}
                  value="/call-center"
                  component={NavLink}
                  to="/call-center"
                />
              )}
              {permissions?.beauty && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/beauty" ? selectedTabStyle : {}),
                  }}
                  label={t("hr.beauty")}
                  value="/beauty"
                  component={NavLink}
                  to="/beauty"
                />
              )}
              {permissions?.htPatient && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/htPatient" ? selectedTabStyle : {}),
                  }}
                  label={t("hr.htPatient")}
                  value="/htPatient"
                  component={NavLink}
                  to="/htPatient"
                />
              )}

              {permissions.operations && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/operations" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.operations")}
                  value="/operations"
                  component={NavLink}
                  to="/operations"
                />
              )}
              {permissions.emergency && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/emergency" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.emergency")}
                  value="/emergency"
                  component={NavLink}
                  to="/emergency"
                />
              )}
              {permissions.bedManagement && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/bed-management" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.bedManagement")}
                  value="/bed-management"
                  component={NavLink}
                  to="/bed-management"
                />
              )}
              {permissions.ipd && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/ipd" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.ipd")}
                  value="/ipd"
                  component={NavLink}
                  to="/ipd"
                />
              )}
              {permissions.icu && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/icu" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.icu")}
                  value="/icu"
                  component={NavLink}
                  to="/icu"
                />
              )}
              {/* Patient Profile - Available to authenticated users only */}
              {token && user?.user && (
                <Tab
                  style={{
                    ...tabStyle,
                    ...(value === "/patient-profile" ? selectedTabStyle : {}),
                  }}
                  label={t("navbar.patientProfile") || "Patient Profile"}
                  value="/patient-profile"
                  component={NavLink}
                  to="/patient-profile"
                />
              )}
            </Tabs>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={2}
          sx={{ display: { xs: "none", md: "flex" } }}
          alignItems={"center"}
          justifyContent={"end"}
        >
          <LanguageSelector />
          <Typography
            variant="body2"
            sx={{
              color: "white",
              fontWeight: "bold",
              fontSize: isExtraSmallScreen ? "0.875rem" : "1rem",
              margin: "0px 5px",
            }} // Responsive font size
          >
            {firstname}
          </Typography>
          <Avatar
            onClick={handleMenuClick}
            sx={{
              cursor: "pointer",
              boxShadow: "0 0 5px rgba(0, 0, 0, 0.3)",
              height: isExtraSmallScreen ? "30px" : "40px", // Responsive size
              width: isExtraSmallScreen ? "30px" : "40px",
            }}
          />
        </Grid>
      </Grid>

      {/* Menu for Avatar */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "8px",
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <MenuItem onClick={handleLogoutClick} sx={{ fontWeight: "500" }}>
          {t("navbar.logout")}
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default NavBar;
